import {
  AspectRatioSpacer,
  HelperText,
  ImagePreview,
  useDropzone,
  useFileInput,
} from '@mint/ui';
import React, { useCallback } from 'react';
import { AdminPanelImageUploaderProps } from './admin-panel-image-uploader-props';
import { CameraIcon } from '@mint/icons';
import { head } from 'lodash';
import styled from 'styled-components';

const Wrapper = styled.div`
  margin-top: 1rem;
`;

const StyledAspectRatioSpacer = styled(AspectRatioSpacer)`
  background: ${props => props.theme.palette.neutral[200]};
  cursor: pointer;
  position: relative;
`;

const StyledCameraIcon = styled(CameraIcon)`
  color: ${props => props.theme.palette.neutral[400]};
  font-size: 3rem;
  top: -0.4rem;
`;

const MessageWrapper = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 300ms;
`;

const StyledImagePreview = styled(ImagePreview)`
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;

const HelperMessage = styled(HelperText)`
  margin-top: 0.2rem;
  user-select: none;
`;

const Image = styled.img`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
`;

export const AdminPanelImageUploader: React.FunctionComponent<
  AdminPanelImageUploaderProps
> = props => {
  const {
    height = 630,
    width = 1200,
    url,
    onFile,
    file,
    helperText,
    ...rest
  } = props;

  const onFiles = useCallback((files: FileList) => {
    const file = head(files);
    if (file && onFile) {
      onFile(file);
    }
  }, []);

  const [state, bindDropzone] = useDropzone({
    onFiles,
  });

  const trigger = useFileInput({
    accept: ['image/*'],
    capture: 'environment',
    multiple: false,
    onChange: onFiles,
  });

  const hideMessageOverlay = !state.dragOver && (url || file);

  return (
    <Wrapper {...rest}>
      <StyledAspectRatioSpacer
        width={width}
        height={height}
        {...bindDropzone}
        onClick={trigger}
      >
        {file && !url && (
          <StyledImagePreview
            file={file}
            width={width}
            height={height}
            filter="blur(50px)"
          />
        )}
        {url && <Image src={`${url}?h=${height}&w=${width}&fit=cover`} />}
        <MessageWrapper style={{ opacity: hideMessageOverlay ? 0 : 0.8 }}>
          <StyledCameraIcon />
        </MessageWrapper>
      </StyledAspectRatioSpacer>
      {helperText && (
        <HelperMessage color="text.hint">{helperText}</HelperMessage>
      )}
    </Wrapper>
  );
};
